'use client'

import React, { useState, useEffect } from 'react'
import { Image, useTranslations, useLocale } from '@/lib/next-shim'
import { SubmitHandler } from 'react-hook-form'
import { useRouter, useSearchParams } from '@/lib/next-shim'
import { useSession } from '@/shared/ui/providers/AuthSessionProvider'

import { ZoomDocNYRegisterForm } from './ZoomDocNYRegisterForm'
import type { ZoomDocNYRegisterFormData } from '../model/zoomDocNYRegisterForm'
import logoGif from '@/assets/img/logo.gif'
import { useSignUpMutation } from '@/core/graphql/hooks'
import { shouldUseCustomBranding } from '@/utils/landing-variant'
import { getEnv } from '@/lib/env'

import { SocialLoginButtons } from '@/shared/ui/buttons/SocialLoginButtons'
import { RegisterSuccess } from './RegisterSuccess'

export function ZoomDocNYRegisterPage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const callbackUrl = searchParams?.get('callbackUrl')
  const isFromBooking = callbackUrl?.includes('meeting-links') || false
  const { status } = useSession()
  const t = useTranslations()

  // Get branding information for white labeling
  const isCustomBranded = shouldUseCustomBranding()
  const brandLogo = getEnv('NEXT_PUBLIC_BRAND_LOGO', '')
  const brandName = getEnv('NEXT_PUBLIC_BRAND_NAME', 'Medconnect Platform')

  // Handle auto-redirect if already authenticated (using useEffect instead of during render)
  useEffect(() => {
    if (status === 'authenticated' && callbackUrl) {
      router.replace(callbackUrl)
    }
  }, [status, router, callbackUrl])

  const [signUpSuccess, setSignUpSuccess] = useState(false)
  const [registrationError, setRegistrationError] = useState<string | null>(null)

  const [signUp, { loading, error }] = useSignUpMutation()
  const locale = useLocale()

  const onSubmit: SubmitHandler<ZoomDocNYRegisterFormData> = (data) => {
    const { terms, confirmPassword, password, ...payload } = data
    // Include the password and language in the signup mutation
    signUp({
      variables: { payload: { ...payload, password, language: locale, callbackUrl: callbackUrl || undefined } },
      onCompleted: async (data, options) => {
        setSignUpSuccess(true)
        window.location.href = data.signUp
      },
      onError: (error) => {
        setRegistrationError(error.message || 'Registration failed. Please try again.')
      },
    })
  }

  const handleLoginClick = () => {
    if (callbackUrl) {
      router.push(`/login?callbackUrl=${encodeURIComponent(callbackUrl)}`)
    } else {
      router.push('/login')
    }
  }

  if (signUpSuccess) {
    return <RegisterSuccess />
  }

  return (
    <div className="h-full flex flex-col">
      {isFromBooking && (
        <div className="bg-blue-50 border-b border-blue-100">
          <div className="max-w-7xl mx-auto py-3 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-2">
              <div className="flex-1 flex items-start sm:items-center min-w-0 break-words">
                <span className="flex-shrink-0 flex p-2 rounded-lg bg-blue-100">
                  <svg className="h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                <p className="ml-3 font-medium text-blue-900 break-words">
                  <span className="block sm:hidden whitespace-normal">{t('page.Register.bookingContext.short')}</span>
                  <span className="hidden sm:block whitespace-normal">{t('page.Register.bookingContext.full')}</span>
                </p>
              </div>
              <div className="flex-shrink-0 w-full sm:w-auto">
                <button
                  onClick={() => router.back()}
                  className="w-full sm:w-auto flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-blue-600 bg-white hover:bg-blue-50"
                >
                  {t('button.back.label')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex-1 flex items-start justify-center bg-white sm:bg-transparent py-5">
        <div className="h-full sm:h-auto overflow-hidden rounded-lg bg-white sm:shadow w-full sm:w-[500px]">
          <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-3xl font-bold tracking-tight text-gray-900">
                  {isCustomBranded ? t('page.Register.signUpToBrand', { brand: brandName }) : t('page.Register.title')}
                </h2>
              </div>
              <button
                onClick={handleLoginClick}
                className="inline-flex items-center justify-center px-4 py-2 border border-primary-200 text-sm font-medium rounded-md text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                type="button"
              >
                {t('button.signIn.label')}
              </button>
            </div>
            {(error || registrationError) && (
              <div className="p-4 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
                {error?.message || registrationError}
              </div>
            )}

            <SocialLoginButtons callbackUrl={callbackUrl || undefined} />

            <div className="mt-4">
              <ZoomDocNYRegisterForm onSubmit={onSubmit} isLoading={loading} />
            </div>
          </div>
        </div>
        <div className="hidden lg:block ml-8">
          <div className="w-[500px] h-[500px] flex justify-center text-center p-8 pt-16">
            <div>
              {isCustomBranded ? (
                <div>
                  <h3 className="text-3xl font-extrabold mb-3">{brandName}</h3>
                  <p className="text-lg text-neutral-400 mb-8">{t('page.Register.tagline')}</p>
                </div>
              ) : (
                t.rich('page.Register.banner', {
                  title: (chunks: React.ReactNode) => <h3 className="text-3xl font-extrabold mb-3">{chunks}</h3>,
                  subtitle: (chunks: React.ReactNode) => <p className="text-lg text-neutral-400 mb-8">{chunks}</p>,
                })
              )}
              {isCustomBranded && brandLogo ? (
                <Image alt={brandName} src={brandLogo} priority width={178} height={100} className="h-24 w-auto object-contain" />
              ) : (
                <Image alt="logo" src={logoGif} priority width={178} height={100} className="h-24" />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
